<template>
  <div>
    <div class="custom-interval-panel">
      <small v-if="infoText" class="block text-color-secondary mb-2">
        <i class="pi pi-info-circle mr-1"></i>{{ infoText }}
      </small>
      <div class="custom-interval-grid">
        <div
          v-for="month in monthsCount"
          :key="month"
          class="flex flex-column gap-1"
          :class="{ 'current-month': month === currentMonth }"
        >
          <label
            :for="`${idPrefix}-month-${month}`"
            class="text-sm text-color-secondary"
            >Month {{ month }}
            <span v-if="month === currentMonth" class="current-month-badge"
              >Current</span
            ></label
          >
          <InputNumber
            :id="`${idPrefix}-month-${month}`"
            :modelValue="modelValue[month - 1]"
            :min="0"
            :minFractionDigits="0"
            :maxFractionDigits="2"
            placeholder="Required"
            class="w-full custom-interval-input"
            :class="{
              'p-invalid': showErrors && modelValue[month - 1] == null,
            }"
            @update:modelValue="(val) => updateMonth(month - 1, val)"
          />
        </div>
      </div>
      <small
        v-if="showErrors && errorMessage && modelValue.some((v) => v == null)"
        class="p-error block mt-2"
      >
        {{ errorMessage }}
      </small>
    </div>

    <Dialog
      v-model:visible="setDefaultIntervalModalVisible"
      header="Set Default Value"
      modal
      :style="{ width: '360px' }"
    >
      <div class="flex flex-column gap-2">
        <label :for="`${idPrefix}-default-value`" class="text-sm"
          >Value will be applied to all empty months (0 is allowed)</label
        >
        <InputNumber
          :id="`${idPrefix}-default-value`"
          v-model="defaultIntervalValue"
          :min="0"
          :minFractionDigits="0"
          :maxFractionDigits="2"
          placeholder="0"
          class="w-full"
          autofocus
        />
      </div>
      <div class="flex gap-2 mt-4 justify-end">
        <Button
          label="Cancel"
          class="p-button-text"
          @click="setDefaultIntervalModalVisible = false"
        />
        <Button
          label="Apply"
          class="p-button-success"
          :disabled="defaultIntervalValue == null"
          @click="applyDefaultIntervalValue"
        />
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref } from "vue";
import InputNumber from "primevue/inputnumber";
import Button from "primevue/button";
import Dialog from "primevue/dialog";

// eslint-disable-next-line no-undef
const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
  monthsCount: {
    type: Number,
    default: 12,
  },
  idPrefix: {
    type: String,
    default: "custom-interval",
  },
  showErrors: {
    type: Boolean,
    default: false,
  },
  infoText: {
    type: String,
    default: "",
  },
  errorMessage: {
    type: String,
    default: "",
  },
  currentMonth: {
    type: Number,
    default: null,
  },
});

// eslint-disable-next-line no-undef
const emit = defineEmits(["update:modelValue"]);

const updateMonth = (index, value) => {
  const next = [...props.modelValue];
  next[index] = value;
  emit("update:modelValue", next);
};

const setDefaultIntervalModalVisible = ref(false);
const defaultIntervalValue = ref(null);

const openDefaultModal = () => {
  defaultIntervalValue.value = null;
  setDefaultIntervalModalVisible.value = true;
};

const applyDefaultIntervalValue = () => {
  if (defaultIntervalValue.value == null) return;
  emit(
    "update:modelValue",
    props.modelValue.map((v) => (v == null ? defaultIntervalValue.value : v))
  );
  setDefaultIntervalModalVisible.value = false;
};

// eslint-disable-next-line no-undef
defineExpose({ openDefaultModal });
</script>

<style scoped>
.custom-interval-panel {
  background: var(--surface-50);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
  padding: 1rem;
}

.custom-interval-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem 1rem;
  width: 100%;
}

.custom-interval-grid > div {
  min-width: 0;
}

.custom-interval-input {
  width: 100%;
}

.custom-interval-input :deep(input) {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  text-align: center;
}

.current-month .custom-interval-input :deep(input) {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 1px var(--primary-color);
}

.current-month-badge {
  margin-left: 0.25rem;
  font-size: 0.65rem;
  font-weight: 700;
  color: var(--primary-color);
  text-transform: uppercase;
}

@media (max-width: 560px) {
  .custom-interval-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
